// Editing an already-approved offer with no reapproval-gating change: skip the
// Approval step and just save. sendMode/create never skip.
export const shouldSkipReapproval = ({
  sendMode,
  type,
  isApprovalEnabled,
  isReapprovalFieldsModified,
}: {
  sendMode: boolean;
  type: 'create' | 'edit';
  isApprovalEnabled: boolean;
  isReapprovalFieldsModified: boolean;
}) =>
  !sendMode &&
  type === 'edit' &&
  isApprovalEnabled &&
  !isReapprovalFieldsModified;

// True when an approval-enabled offer isn't approved_internal yet, so sending/previewing must
// route to approval first. Non-approval and unknown-status offers don't block.
export const offerNeedsApprovalBeforeSend = ({
  isApprovalEnabled,
  offerStatus,
}: {
  isApprovalEnabled: boolean;
  offerStatus?: string;
}) => isApprovalEnabled && !!offerStatus && offerStatus !== 'approved_internal';

// Statuses with no generated document yet (resumed draft, or reverted to draft after a
// re-approval edit); these open in edit mode but behave like create for draft/skip (CORE-4822).
export const DRAFT_LIKE_OFFER_STATUSES = [
  'draft',
  'pending_document_generation',
  'document_generation_error',
];

export const isDraftLikeOffer = (offerStatus?: string): boolean =>
  !!offerStatus && DRAFT_LIKE_OFFER_STATUSES.includes(offerStatus);

// react-datepicker renders an open calendar into a shared body div (DateField's
// DATEPICKER_PORTAL_ID). As the user advances a step the previous step's input is only
// display:none'd (still mounted), so a calendar left open keeps floating over the new
// step. Closing any open calendar on step change fixes that — and, unlike hiding the
// portal, leaves it usable for the new step's own date fields, e.g. the inline
// empty-tokens form on the generation step (CORE-4852).
const DATEPICKER_PORTAL_ID = 'datepicker-portal';

export const closeStaleDatePicker = (): void => {
  const portal = document.getElementById(DATEPICKER_PORTAL_ID);
  // Only act when a calendar is actually open, to avoid a needless global event.
  if (!portal?.querySelector('.react-datepicker')) return;
  // Dispatch the same "outside" event react-datepicker closes on (its click-outside
  // listens for mousedown on document) so React tears the calendar down properly.
  document.dispatchEvent(new MouseEvent('mousedown', { bubbles: true }));
};
// "Skip offer generation" shows on the details step when approval + flag are on; in edit mode
// only for offers with no generated document yet (skip-doc offers or resumed drafts).
export const shouldShowSkipDocButton = ({
  stepId,
  isApprovalEnabled,
  skipDocFlagEnabled,
  type,
  documentGenerationSkipped,
  isDraft,
}: {
  stepId?: string;
  isApprovalEnabled: boolean;
  skipDocFlagEnabled: boolean;
  type: 'create' | 'edit';
  documentGenerationSkipped?: boolean;
  isDraft?: boolean;
}) => {
  const skipAllowedForEdit =
    type !== 'edit' || !!documentGenerationSkipped || !!isDraft;
  return (
    stepId === 'offerDetails' &&
    isApprovalEnabled &&
    skipDocFlagEnabled &&
    skipAllowedForEdit
  );
};

// Which "Send offer" options are available. Kula is always on; e-sign providers
// only when the offer reports them connected (send_with_*_enabled).
export const connectedSignatureIds = (offer?: {
  send_with_docusign_enabled?: boolean;
  send_with_pandadoc_enabled?: boolean;
  send_with_adobe_enabled?: boolean;
}): string[] =>
  [
    'kula',
    offer?.send_with_docusign_enabled ? 'docusign' : null,
    offer?.send_with_pandadoc_enabled ? 'pandadoc' : null,
    offer?.send_with_adobe_enabled ? 'adobe' : null,
  ].filter(Boolean) as string[];

// Re-seeds the offerAttachments store from the full offer GET on first load or offer change so a
// stale global store re-syncs (CORE-4803); keeps the store as-is for the same offer so session
// add/remove aren't clobbered by a refetch (CORE-4796). Returns null = leave the store as-is.
export const nextSeededAttachments = <T>(
  currentOfferId: number | string | null | undefined,
  serverAttachments: T[] | undefined,
  storeAttachments: T[] | undefined,
  lastSeededOfferId: number | string | null
): { attachments: T[]; offerId: number | string } | null => {
  if (currentOfferId === null || currentOfferId === undefined) return null;

  // Seed only when the offer changed; for the same offer the store owns session add/remove, so
  // re-seeding would undo deletions from the stale offer-query cache (CORE-4800).
  const offerChanged = lastSeededOfferId !== currentOfferId;
  if (offerChanged) {
    return { attachments: serverAttachments ?? [], offerId: currentOfferId };
  }
  return null;
};

export const stabilizeOfferSeedRowIdToCfIdMap = (
  atsFormFields:
    | Array<{ id?: unknown; ats_custom_field_id?: unknown }>
    | undefined,
  offerFieldIds: Set<string>,
  prevMap: Record<string, string>
): Record<string, string> => {
  const merged: Record<string, string> = { ...prevMap };
  (atsFormFields || []).forEach((f) => {
    if (
      f?.ats_custom_field_id !== null &&
      f?.ats_custom_field_id !== undefined &&
      f?.id !== null &&
      f?.id !== undefined &&
      offerFieldIds.has(String(f.id))
    ) {
      merged[String(f.id)] = String(f.ats_custom_field_id);
    }
  });
  const signature = (m: Record<string, string>): string =>
    Object.keys(m)
      .sort()
      .map((k) => `${k}:${m[k]}`)
      .join(',');
  return signature(merged) === signature(prevMap) ? prevMap : merged;
};

interface OfferEmailFile {
  url?: string;
  content_type?: string;
  filename?: string;
  isNonRemovable?: boolean;
}

// Offer email attachments: generated PDFs (multi-doc array, else single PDF) as non-removable,
// then additionalFiles from the fresh offer GET so template-step uploads aren't stale (CORE-4800).
export const buildOfferEmailAttachments = (
  currentOffer:
    | {
        offer_documents_pdf?: OfferEmailFile[];
        offer_document_pdf?: OfferEmailFile;
      }
    | undefined,
  additionalFiles: OfferEmailFile[] = []
): OfferEmailFile[] => {
  const multiDocPdfs = (currentOffer?.offer_documents_pdf || [])
    .filter((p) => p?.url)
    .map((p) => ({ ...p, isNonRemovable: true }));
  if (multiDocPdfs.length > 0) {
    return [...multiDocPdfs, ...additionalFiles];
  }
  const offerPDF = currentOffer?.offer_document_pdf?.url
    ? [{ ...currentOffer.offer_document_pdf, isNonRemovable: true }]
    : [];
  return [...offerPDF, ...additionalFiles];
};
